import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { filterConstants } from "../../constants";
import {
  useGetPageQueryParams,
  useMerchantApiQueryWithParams,
  useQueryString,
  useQuickSubmitFormHelper,
} from "../../hooks";
import {
  IQueryStringNavLinks,
  ITableHeader,
  IViewPayout,
  IPageAndSearchRequest,
  PagedMeta,
} from "../../interfaces-and-types";
import { View } from "./View";
import { QuickSubmitForm } from "../../components";

interface IPayoutQueryMeta {
  status: string | null;
}

export interface IPayoutViewProps {
  data: IViewPayout[];
  headers: ITableHeader[];
  isLoading: boolean;
  isError: boolean;
  totalCount: number;
  queryStringTabs: IQueryStringNavLinks;

  // modals
  isCreateNewModalOpen: boolean;
  closeModal: () => void;
  refreshTable: () => void;

  // extra buttons
  onExport: () => void;
  openCreateNewModal: () => void;
}

export const Payouts = () => {
  const queryClient = useQueryClient();
  const query = useQueryString();
  const status = query.get(filterConstants.statusKey);
  const { page, page_size, search } = useGetPageQueryParams();

  const {
    isLoading: isLoadingPayouts,
    isError: isErrorPayouts,
    data: payouts,
    meta: payoutsMeta,
  } = useMerchantApiQueryWithParams<
    IPageAndSearchRequest & IPayoutQueryMeta,
    IViewPayout[],
    PagedMeta
  >([
    "getPayouts",
    {
      page: (page && Number(page)) || 1,
      page_size: (page_size && Number(page_size)) || 10,
      search,
      status,
    },
  ]);

  const [isCreateNewModalOpen, setCreateNewModalOpen] = useState(false);
  const closeModal = () => {
    setCreateNewModalOpen(false);
  };

  const headers: ITableHeader[] = [
    {
      key: "amount",
      header: "Amount",
    },
    {
      key: "bank_name",
      header: "Bank Name",
    },
    {
      key: "account_number",
      header: "Account No",
    },
    {
      key: "account_name",
      header: "Account Name",
    },
    {
      key: "status",
      header: "Status",
    },
    // {
    //   key: "description",
    //   header: "Description",
    // },
    {
      key: "created_at",
      header: "Created On",
    },
    {
      key: "paid_at",
      header: "Paid On",
    },
    {
      key: "more",
      header: "",
    },
  ];

  const queryStringTabs: IQueryStringNavLinks = {
    query: filterConstants.statusKey,
    options: [
      {
        key: "All",
        value: null,
      },
      {
        key: "Settled",
        value: "successful",
      },
      {
        key: "Processing",
        value: "processing",
      },
      {
        key: "Failed",
        value: "failed",
      },
    ],
  };

  const onExport = () => {
    alert("exporting data...");
  };
  const openCreateNewModal = () => {
    setCreateNewModalOpen(true);
  };

  const refreshTable = () => {
    queryClient.invalidateQueries([
      "getPayouts",
      {
        page: (page && Number(page)) || 1,
        page_size: (page_size && Number(page_size)) || 10,
        search,
        status: status || "active",
      },
    ]);
  };

  const payoutsWithRoutes: IViewPayout[] | undefined = payouts?.map((po) => ({
    ...po,
    openRetryPayoutModal: () => openRetryPayoutModal(po.id, false),
  }));

  const {
    isModalOpen: isRetryPayoutModalOpen,
    openModal: openRetryPayoutModal,
    closeModal: closeRetryPayoutModal,

    isLoading: isLoadingRetryPayout,
    isSuccess: isSuccessRetryPayout,
    mutate: retryPayout,
  } = useQuickSubmitFormHelper("retryFailedPayoutById", refreshTable, "POST");

  return (
    <>
      <View
        data={payoutsWithRoutes || []}
        headers={headers}
        isLoading={isLoadingPayouts}
        isError={isErrorPayouts}
        totalCount={payoutsMeta?.page_meta.item_count || 0}
        queryStringTabs={queryStringTabs}
        onExport={onExport}
        openCreateNewModal={openCreateNewModal}
        isCreateNewModalOpen={isCreateNewModalOpen}
        closeModal={closeModal}
        refreshTable={refreshTable}
      />

      <QuickSubmitForm
        formTitle="Retry Payout"
        isModalOpen={isRetryPayoutModalOpen}
        closeModal={closeRetryPayoutModal}
        isDeleteLocked={false}
        isSubmitting={isLoadingRetryPayout}
        isSuccess={isSuccessRetryPayout}
        onButtonClick={retryPayout}
        buttonKind="success"
        buttonText="Initiate Retry"
        confirmationMessage={
          <>
            <h4>Are you sure you want to retry this payout?</h4>
            <br />

            <span className="text-danger">
              (NOTE: Payout of the settlement amount to your specified bank
              account will be re-attempted)
            </span>
          </>
        }
      />
    </>
  );
};
